import { riskScore, isScored, isOpenRisk, highRiskThreshold } from "./calc.js";
import { statusState } from "../theme/tokens.js";

/* One card per school, the way the team's own permitting dashboard reads.
 *
 * The registers already file both permits and risks against a site, which for
 * VIDE is the school, so nothing new has to be imported and nothing has to be
 * matched between systems. Edit a row and this moves.
 *
 * The colour is about risk, not progress. That is deliberate and it is how the
 * team's spreadsheet already works: a school at 0 per cent with no risks on it
 * is green, because there is nothing wrong there, only work not yet done. A
 * school at 0 per cent carrying two criticals is the one somebody has to go
 * and deal with today.
 */

export const BANDS = ["critical", "high", "medium", "low"];

/* A register that rates in words rather than numbers, which the WAPA RAID log
   does and imported school registers often do. The words people actually
   write, not the words a dropdown would offer. */
const RATED = {
  critical: ["critical", "severe", "very high", "red"],
  high: ["high", "major", "amber", "orange"],
  medium: ["medium", "med", "moderate", "yellow"],
  low: ["low", "minor", "green"],
};

const wordBand = (value) => {
  const word = String(value ?? "").trim().toLowerCase();
  if (!word) return null;
  for (const band of BANDS) {
    if (RATED[band].includes(word)) return band;
  }
  return null;
};

/* Where the critical line sits when a register scores rather than rates.
 *
 * The contract already carries the line for high, because a register scoring
 * one to three cannot use the same line as one scoring one to five. Critical
 * is a step above it on that same scale: a third higher, rounded up. On a
 * five by five register with high at 15 that puts critical at 20, and on
 * VIDE's with high at 9 it puts it at 12.
 */
export const criticalThreshold = (contract) =>
  Math.ceil(highRiskThreshold(contract) * 4 / 3);

export function riskBand(risk, contract) {
  const rated = wordBand(risk?.rating);
  if (rated) return rated;
  if (!isScored(risk)) return null;
  const score = riskScore(risk);
  if (score >= criticalThreshold(contract)) return "critical";
  if (score >= highRiskThreshold(contract)) return "high";
  /* Half the high line, rounded down so it lands on a score the register can
     actually produce. VIDE rates each axis L, M, H, C as 1 to 4, so its high
     line of 9 puts medium at 4, which is medium by medium. Half of 9 is 4.5
     and would have called medium by medium low. */
  if (score >= Math.floor(highRiskThreshold(contract) / 2)) return "medium";
  return "low";
}

/* Critical outranks high outranks everything. A school shows the worst thing
   on it, because the worst thing on it is what decides whether somebody goes
   there this week. */
export const cardBand = (counts) =>
  (counts.critical > 0 ? "critical" : counts.high > 0 ? "high" : "clear");

/* Rows that belong to the program rather than to one place. The VIDE
   register files these against School "ALL", and they are real risks: WAPA
   power reliability across all islands is not a problem at one school.
   Letting them colour every card would turn the whole board dark the moment
   one of them opened, and a board where everything is dark tells nobody
   where to go. They are counted on their own instead. */
const PROGRAM_WIDE = /^(all|all schools|all sites|program(me)?[\s-]wide|territory[\s-]wide|multiple)$/i;

export const isProgramWide = (row) => PROGRAM_WIDE.test(String(row?.site ?? "").trim());

const siteOf = (row) => {
  const name = String(row?.site ?? "").trim();
  return PROGRAM_WIDE.test(name) ? "" : name;
};

/* Finished, however this particular register spells it. The VIDE permitting
   log says Closed, an imported workbook says Completed, an agency log says
   Approved, and all three mean the permit is done. Asking the app's shared
   vocabulary rather than picking one spelling is what stops a gauge reading
   nought per cent on a school where everything is through. */
const isDone = (p) => statusState(p?.status) === "complete";

const percent = (done, total) => (total > 0 ? Math.round((done / total) * 100) : 0);

export function siteBoard({ permits = [], risks = [], contract } = {}) {
  const byName = new Map();
  const seat = (name) => {
    if (!byName.has(name)) {
      byName.set(name, {
        site: name, region: "",
        permits: { total: 0, closed: 0, open: 0, percent: 0, records: { total: [], closed: [], open: [] } },
        risks: {
          total: 0, critical: 0, high: 0, medium: 0, low: 0, unrated: 0,
          records: { total: [], critical: [], high: [], medium: [], low: [] },
        },
      });
    }
    return byName.get(name);
  };

  /* Rows with no site are not dropped quietly. A permit nobody filed against a
     school is a permit nobody is watching, and the screen says how many. */
  const loose = { permits: [], risks: [] };
  const wide = { permits: [], risks: [] };

  for (const p of permits) {
    const name = siteOf(p);
    if (!name) { (isProgramWide(p) ? wide.permits : loose.permits).push(p); continue; }
    const card = seat(name);
    if (!card.region && p.region) card.region = String(p.region).trim();
    card.permits.total += 1;
    card.permits.records.total.push(p);
    if (isDone(p)) { card.permits.closed += 1; card.permits.records.closed.push(p); }
    else { card.permits.open += 1; card.permits.records.open.push(p); }
  }

  /* Closed risks are history and do not colour a card. A school that worked
     through five criticals last quarter is not still critical. */
  for (const r of risks.filter(isOpenRisk)) {
    const name = siteOf(r);
    if (!name) { (isProgramWide(r) ? wide.risks : loose.risks).push(r); continue; }
    const card = seat(name);
    if (!card.region && r.region) card.region = String(r.region).trim();
    card.risks.total += 1;
    card.risks.records.total.push(r);
    const band = riskBand(r, contract);
    if (!band) { card.risks.unrated += 1; continue; }
    card.risks[band] += 1;
    card.risks.records[band].push(r);
  }

  const cards = [...byName.values()].map((card) => ({
    ...card,
    permits: { ...card.permits, percent: percent(card.permits.closed, card.permits.total) },
    band: cardBand(card.risks),
  }));

  /* Worst first, then least finished, then by name. The top left of the board
     is where the attention should go. */
  const rank = { critical: 0, high: 1, clear: 2 };
  cards.sort((a, b) =>
    rank[a.band] - rank[b.band]
    || a.permits.percent - b.permits.percent
    || a.site.localeCompare(b.site));

  const wideCounts = { critical: 0, high: 0, medium: 0, low: 0, unrated: 0,
                       records: { critical: [], high: [], medium: [], low: [] } };
  for (const r of wide.risks) {
    const band = riskBand(r, contract);
    if (!band) { wideCounts.unrated += 1; continue; }
    wideCounts[band] += 1;
    wideCounts.records[band].push(r);
  }

  return {
    cards,
    programWide: { ...wideCounts, total: wide.risks.length, risks: wide.risks, permits: wide.permits },
    unassigned: { permits: loose.permits, risks: loose.risks },
    totals: {
      sites: cards.length,
      critical: cards.filter((c) => c.band === "critical").length,
      high: cards.filter((c) => c.band === "high").length,
      clear: cards.filter((c) => c.band === "clear").length,
    },
  };
}
